/**
 * AccessibilityProvider.tsx
 * ------------------------
 * Provides accessibility context (high contrast, font size) to the app.
 * - Context API for accessibility settings.
 * - Applies global styles based on context.
 */


import React, { createContext, useContext, useState } from 'react';

interface AccessibilityContextType {
  isHighContrast: boolean;
  fontSize: string;
  toggleHighContrast: () => void;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
}

const AccessibilityContext = createContext<AccessibilityContextType>({
  isHighContrast: false,
  fontSize: 'medium',
  toggleHighContrast: () => {},
  increaseFontSize: () => {},
  decreaseFontSize: () => {},
});

export const AccessibilityProvider: React.FC<React.PropsWithChildren<object>> = ({ children }) => {
  const [isHighContrast, setIsHighContrast] = useState(false);
  const [fontSize, setFontSize] = useState('medium');

  const toggleHighContrast = () => {
    setIsHighContrast((prev) => !prev);
  };

  const increaseFontSize = () => {
    setFontSize((prev) => {
      if (prev === 'medium') return 'large';
      if (prev === 'small') return 'medium';
      return prev;
    });
  };

  const decreaseFontSize = () => {
    setFontSize((prev) => {
      if (prev === 'large') return 'medium';
      if (prev === 'medium') return 'small';
      return prev;
    });
  };

  const contextValue = React.useMemo(
    () => ({
      isHighContrast,
      fontSize,
      toggleHighContrast,
      increaseFontSize,
      decreaseFontSize,
    }),
    [isHighContrast, fontSize],
  );

  // Extract font size class
  let fontSizeClass = 'text-base';
  if (fontSize === 'large') {
    fontSizeClass = 'text-xl';
  } else if (fontSize === 'small') {
    fontSizeClass = 'text-sm';
  }

  return (
    <AccessibilityContext.Provider value={contextValue}>
      <div
        className={`flex flex-col min-h-screen max-w-full
            ${isHighContrast ? 'bg-black text-white' : 'bg-white text-black'}
            ${fontSizeClass}
            sm:text-base md:text-lg lg:text-xl 
          `}
      >
        {children}
      </div>
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => {
  return useContext(AccessibilityContext);
};
